// src/modules/myvault/hooks/useAutomationRules.ts

// What? Data-fetching + mutation hooks for the MyVault automation rule API.
// Why? R-3 ships a full rules UI; these hooks encapsulate the wire format and
//      the `x-workspace-id` header wiring so view components don't have to
//      know about either.
// How? Follows the existing useCanvases pattern: authFetch + workspace header
//      per call, abortable fetches, simple loading/error/refresh surface.

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";

const AUTOMATION_API_BASE = "/api/workspace/myvault/automation";

const getWorkspaceHeaders = (workspaceId: string): Record<string, string> => ({
  "x-workspace-id": workspaceId,
});

export type TriggerType =
  | "ROW_CREATED"
  | "FIELD_CHANGED"
  | "FIELD_MATCHES"
  | "BUTTON_CLICK"
  | "SCHEDULED";

export type ActionType =
  | "SEND_EMAIL"
  | "CREATE_RECORD"
  | "UPDATE_FIELD"
  | "RUN_SKILL"
  | "WEBHOOK"
  | "INSTANTIATE_TEMPLATE";

export interface AutomationRuleAction {
  type: ActionType;
  config: Record<string, unknown>;
}

export interface AutomationConditionLeaf {
  field: string;
  operator:
    | "eq"
    | "neq"
    | "gt"
    | "lt"
    | "gte"
    | "lte"
    | "contains"
    | "in"
    | "is_empty"
    | "is_not_empty"
    | "changed"
    | "changed_to"
    | "changed_from";
  value?: unknown;
}

export interface AutomationConditionGroup {
  op: "AND" | "OR";
  operands: (AutomationConditionLeaf | AutomationConditionGroup)[];
}

export type AutomationCondition =
  | AutomationConditionLeaf
  | AutomationConditionGroup;

export interface AutomationRule {
  id: string;
  workspace_id: string;
  table_id: string;
  name: string;
  enabled: boolean;
  allow_cascading: boolean;
  trigger_type: TriggerType;
  trigger_config: Record<string, unknown>;
  conditions: AutomationCondition | null;
  actions: AutomationRuleAction[];
  created_by: string;
  created_at: string;
  updated_at: string;
  last_executed_at: string | null;
  execution_count: number;
}

export interface AutomationRuleCreate {
  table_id: string;
  name: string;
  enabled?: boolean;
  allow_cascading?: boolean;
  trigger_type: TriggerType;
  trigger_config?: Record<string, unknown>;
  conditions?: AutomationCondition | null;
  actions: AutomationRuleAction[];
}

export type AutomationRuleUpdate = Partial<AutomationRuleCreate>;

export interface AutomationExecution {
  id: string;
  workspace_id: string;
  rule_id: string;
  trigger_type: TriggerType;
  triggered_by: string;
  triggered_at: string;
  table_id: string;
  row_id: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  status:
    | "pending"
    | "running"
    | "condition_not_met"
    | "no_matches"
    | "success"
    | "partial_failure"
    | "failure"
    | "skipped";
  actions_results: Record<string, unknown>[];
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
}

export interface AutomationTestResult {
  would_fire: boolean;
  condition_result: boolean;
  rule_id: string;
  row_id: string;
  row_data_sample: Record<string, string>;
}

export interface UseAutomationRulesFilters {
  tableId?: string;
  enabled?: boolean;
}

/** List all rules for the workspace with optional table/enabled filters. */
export const useAutomationRules = (
  workspaceId: string,
  filters?: UseAutomationRulesFilters,
) => {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchRules = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters?.tableId) params.set("table_id", filters.tableId);
      if (filters?.enabled !== undefined) {
        params.set("enabled", String(filters.enabled));
      }
      const qs = params.toString();
      const url = `${AUTOMATION_API_BASE}/rules${qs ? `?${qs}` : ""}`;

      const response = await authFetchRef.current(
        url,
        "fetch automation rules",
        {
          headers: getWorkspaceHeaders(workspaceId),
        },
      );
      if (!signal.aborted) {
        setRules(Array.isArray(response) ? response : []);
        setError(null);
      }
    } catch (err) {
      if (!signal.aborted) {
        const message =
          err instanceof Error ? err.message : "Failed to fetch rules";
        setError(message);
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [workspaceId, filters?.tableId, filters?.enabled]);

  useEffect(() => {
    fetchRules();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchRules]);

  return {
    rules,
    loading,
    error,
    refresh: fetchRules,
  };
};

/** Fetch a single rule by id. */
export const useAutomationRule = (
  workspaceId: string,
  ruleId: string | null,
) => {
  const [rule, setRule] = useState<AutomationRule | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(ruleId));
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const load = useCallback(async () => {
    if (!workspaceId || !ruleId) {
      setRule(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await authFetchRef.current(
        `${AUTOMATION_API_BASE}/rules/${encodeURIComponent(ruleId)}`,
        "fetch automation rule",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setRule(data as AutomationRule);
      setError(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to fetch rule";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, ruleId]);

  useEffect(() => {
    load();
  }, [load]);

  return { rule, loading, error, refresh: load };
};

/** Mutation hooks for create / update / delete / toggle / test. */
export const useAutomationRuleMutations = (workspaceId: string) => {
  const authFetch = useAuthFetch();

  const createRule = useCallback(
    async (body: AutomationRuleCreate): Promise<AutomationRule> => {
      return await authFetch(
        `${AUTOMATION_API_BASE}/rules`,
        "create automation rule",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getWorkspaceHeaders(workspaceId),
          },
          body: JSON.stringify(body),
        },
      );
    },
    [authFetch, workspaceId],
  );

  const updateRule = useCallback(
    async (
      ruleId: string,
      body: AutomationRuleUpdate,
    ): Promise<AutomationRule> => {
      return await authFetch(
        `${AUTOMATION_API_BASE}/rules/${encodeURIComponent(ruleId)}`,
        "update automation rule",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getWorkspaceHeaders(workspaceId),
          },
          body: JSON.stringify(body),
        },
      );
    },
    [authFetch, workspaceId],
  );

  const deleteRule = useCallback(
    async (ruleId: string): Promise<void> => {
      await authFetch(
        `${AUTOMATION_API_BASE}/rules/${encodeURIComponent(ruleId)}`,
        "delete automation rule",
        {
          method: "DELETE",
          headers: getWorkspaceHeaders(workspaceId),
        },
      );
    },
    [authFetch, workspaceId],
  );

  const toggleRule = useCallback(
    async (ruleId: string): Promise<AutomationRule> => {
      return await authFetch(
        `${AUTOMATION_API_BASE}/rules/${encodeURIComponent(ruleId)}/toggle`,
        "toggle automation rule",
        {
          method: "POST",
          headers: getWorkspaceHeaders(workspaceId),
        },
      );
    },
    [authFetch, workspaceId],
  );

  const testRule = useCallback(
    async (ruleId: string, rowId: string): Promise<AutomationTestResult> => {
      return await authFetch(
        `${AUTOMATION_API_BASE}/rules/${encodeURIComponent(ruleId)}/test`,
        "test automation rule",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getWorkspaceHeaders(workspaceId),
          },
          body: JSON.stringify({ row_id: rowId }),
        },
      );
    },
    [authFetch, workspaceId],
  );

  return { createRule, updateRule, deleteRule, toggleRule, testRule };
};

export interface UseAutomationExecutionsFilters {
  ruleId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

/** List executions (newest first). */
export const useAutomationExecutions = (
  workspaceId: string,
  filters?: UseAutomationExecutionsFilters,
) => {
  const [executions, setExecutions] = useState<AutomationExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchExecutions = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters?.ruleId) params.set("rule_id", filters.ruleId);
      if (filters?.status) params.set("status", filters.status);
      if (filters?.limit !== undefined) {
        params.set("limit", String(filters.limit));
      }
      if (filters?.offset !== undefined) {
        params.set("offset", String(filters.offset));
      }
      const qs = params.toString();
      const url = `${AUTOMATION_API_BASE}/executions${qs ? `?${qs}` : ""}`;

      const data = await authFetchRef.current(url, "fetch execution log", {
        headers: getWorkspaceHeaders(workspaceId),
      });
      if (!signal.aborted) {
        setExecutions(Array.isArray(data) ? data : []);
        setError(null);
      }
    } catch (err) {
      if (!signal.aborted) {
        const message =
          err instanceof Error ? err.message : "Failed to fetch executions";
        setError(message);
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [
    workspaceId,
    filters?.ruleId,
    filters?.status,
    filters?.limit,
    filters?.offset,
  ]);

  useEffect(() => {
    fetchExecutions();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchExecutions]);

  return {
    executions,
    loading,
    error,
    refresh: fetchExecutions,
  };
};

/** Fetch a single execution by id. */
export const useAutomationExecution = (
  workspaceId: string,
  execId: string | null,
) => {
  const [execution, setExecution] = useState<AutomationExecution | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(execId));
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const load = useCallback(async () => {
    if (!workspaceId || !execId) {
      setExecution(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await authFetchRef.current(
        `${AUTOMATION_API_BASE}/executions/${encodeURIComponent(execId)}`,
        "fetch execution detail",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setExecution(data as AutomationExecution);
      setError(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to fetch execution";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, execId]);

  useEffect(() => {
    load();
  }, [load]);

  return { execution, loading, error, refresh: load };
};
